// Tenantra Inc. - Tenant Detail Page (Microsoft 365 Admin Style)
import { useEffect, useState } from 'react';
import { 
  ArrowLeft, User, Calendar, Home, DollarSign, FileText, 
  CheckCircle, Clock, XCircle, AlertTriangle, Hash, Mail, Phone,
  ChevronRight, Plus
} from 'lucide-react';
import { toast } from 'sonner';
import { TableSkeleton } from '@/components/dashboard/TableKit';
import api, { apiHelpers } from '@/services/api';

const PAYMENT_STATUS = {
  on_time: { label: 'On Time', dotColor: 'bg-emerald-500', textColor: 'text-emerald-600' },
  late: { label: 'Late', dotColor: 'bg-amber-500', textColor: 'text-amber-600' },
  partial: { label: 'Partial', dotColor: 'bg-amber-500', textColor: 'text-amber-600' },
  missed: { label: 'Missed', dotColor: 'bg-red-500', textColor: 'text-red-600' }
};

const TENANCY_STATUS = {
  active: { label: 'Active', dotColor: 'bg-emerald-500', textColor: 'text-emerald-600' },
  pending_consent: { label: 'Pending', dotColor: 'bg-amber-500', textColor: 'text-amber-600' },
  ended: { label: 'Ended', dotColor: 'bg-slate-400', textColor: 'text-slate-500' }
};

export default function TenantDetailPage() {
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [screeningDocs, setScreeningDocs] = useState({ app_id: null, documents: [] });

  // Get tenant ID from URL
  const tenantId = window.location.pathname.split('/')[3];

  useEffect(() => {
    if (tenantId) {
      fetchTenantDetails();
      api.get(`/esign/tenants/${tenantId}/documents`).then(r => setDocuments(r.data.documents || [])).catch(() => {});
      api.get(`/screening/tenants/${tenantId}/documents`).then(r => setScreeningDocs(r.data)).catch(() => {});
    }
  }, [tenantId]);

  const fetchTenantDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/tenants/${tenantId}`);
      setTenant(response.data);
    } catch (err) {
      toast.error(apiHelpers.handleError(err));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const [year, month, day] = dateStr.split('T')[0].split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-CA', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric' 
    });
  };

  if (loading) {
    return <TableSkeleton rows={5} spans={[5, 3, 2, 2]} />;
  }

  if (!tenant) {
    return (
      <div className="text-center py-12">
        <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center mx-auto mb-3">
          <User className="w-5 h-5 text-slate-400" />
        </div>
        <h3 className="text-base font-medium text-slate-900 mb-1">Tenant not found</h3>
        <a href="/landlord/tenants" className="text-sm text-teal-600 hover:text-teal-700 font-medium">
          Back to tenants
        </a>
      </div>
    );
  }

  const activeCount = tenant.tenancies?.filter(t => t.status === 'active').length || 0;
  const endedCount = tenant.tenancies?.filter(t => t.status === 'ended').length || 0;

  return (
    <div className="space-y-5">
      {/* Back Link */}
      <a 
        href="/landlord/tenants" 
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 font-medium"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to tenants
      </a>

      {/* Header - Compact */}
      <div className="flex items-center gap-3">
        <div className="min-w-0">
          <h1 className="text-lg font-semibold text-slate-800 truncate">{tenant.full_name}</h1>
          <p className="text-xs text-slate-500 font-mono">{tenant.tenant_unique_id || '------'}</p>
        </div>
      </div>

      {/* Stats Row */}
      <div className="flex items-center gap-6 py-2 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <span className="text-xl font-semibold text-emerald-600">{activeCount}</span>
          <span className="text-sm text-slate-500">Active</span>
        </div>
        <div className="w-px h-5 bg-slate-200" />
        <div className="flex items-center gap-2">
          <span className="text-xl font-semibold text-slate-400">{endedCount}</span>
          <span className="text-sm text-slate-500">Ended</span>
        </div>
        <div className="w-px h-5 bg-slate-200" />
        <div className="flex items-center gap-2">
          <span className="text-xl font-semibold text-teal-600">{tenant.report_count || 0}</span>
          <span className="text-sm text-slate-500">Reports</span>
        </div>
      </div>

      {/* Contact Info - Table style */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200">
          <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Contact information</span>
        </div>
        <div className="divide-y divide-slate-100">
          <div className="px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Calendar className="w-4 h-4" />
              Date of birth
            </div>
            <span className="text-sm text-slate-800 font-medium">{formatDate(tenant.date_of_birth)}</span>
          </div>
          {tenant.email && (
            <div className="px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Mail className="w-4 h-4" />
                Email
              </div>
              <span className="text-sm text-slate-800">{tenant.email}</span>
            </div>
          )}
          {tenant.phone && (
            <div className="px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Phone className="w-4 h-4" />
                Phone
              </div>
              <span className="text-sm text-slate-800">{tenant.phone}</span>
            </div>
          )}
        </div>
      </div>

      {/* Tenancy History - Table style */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
          <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Tenancy history</span>
          {tenant.tenancies?.length > 0 && (
            <span className="text-xs text-slate-400">{tenant.tenancies.length} record{tenant.tenancies.length !== 1 ? 's' : ''}</span>
          )}
        </div>
        
        {tenant.tenancies?.length > 0 ? (
          <div className="divide-y divide-slate-100">
            {tenant.tenancies.map((tenancy, index) => {
              const statusConfig = TENANCY_STATUS[tenancy.status] || TENANCY_STATUS.ended;
              
              return (
                <div key={tenancy.id || index} className="px-4 py-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="w-7 h-7 rounded bg-slate-100 flex items-center justify-center flex-shrink-0">
                        <Home className="w-3.5 h-3.5 text-slate-500" />
                      </div>
                      <span className="text-sm font-medium text-slate-800 truncate">{tenancy.property_address}</span>
                    </div>
                    <span className={`inline-flex items-center gap-1.5 text-xs ${statusConfig.textColor}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${statusConfig.dotColor}`}></span>
                      {statusConfig.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-slate-500 ml-9">
                    <span>{formatDate(tenancy.move_in_date)} — {formatDate(tenancy.move_out_date)}</span>
                    <span>${parseFloat(tenancy.rent_amount || 0).toLocaleString()}/mo</span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="px-4 py-8 text-center">
            <p className="text-sm text-slate-500">No tenancy history with you</p>
          </div>
        )}
      </div>

      {/* Documents */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="tenant-documents-card">
        <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
          <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Documents</span>
          {documents.length > 0 && <span className="text-xs text-slate-400">{documents.length} record{documents.length !== 1 ? 's' : ''}</span>}
        </div>
        {documents.length > 0 ? (
          <div className="divide-y divide-slate-100">
            {documents.map((doc) => (
              <DocumentRow key={doc.id} doc={doc} formatDate={formatDate} />
            ))}
          </div>
        ) : (
          <div className="px-4 py-6 text-center">
            <p className="text-sm text-slate-500">No signed documents yet</p>
          </div>
        )}
      </div>

      {/* Screening documents (uploaded at application, follow the tenant) */}
      {screeningDocs.documents?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="tenant-screening-docs-card">
          <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Screening documents</span>
            <span className="text-xs text-slate-400">{screeningDocs.documents.length} file{screeningDocs.documents.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="divide-y divide-slate-100">
            {screeningDocs.documents.map((d) => (
              <ScreeningDocRow key={d.file_id} appId={screeningDocs.app_id} doc={d} />
            ))}
          </div>
        </div>
      )}

      {/* Actions Row */}
      <div className="flex items-center gap-4 pt-2">
        {tenant.tenancies?.find(t => t.status === 'active') && (
          <a 
            href={`/landlord/tenants/${tenantId}/end?tenancy=${tenant.tenancies.find(t => t.status === 'active')?.id}`}
            className="text-sm text-slate-500 hover:text-slate-700 font-medium inline-flex items-center gap-1.5 hover:underline"
          >
            End tenancy
          </a>
        )}
        {tenant.tenancies?.[0]?.id && (
          <a 
            href={`/landlord/reports/${tenant.tenancies[0].id}/new`}
            className="text-sm text-teal-600 hover:text-teal-700 font-medium inline-flex items-center gap-1.5 hover:underline"
          >
            <Plus className="w-4 h-4" />
            Add report
          </a>
        )}
      </div>
    </div>
  );
}

const SCREEN_DOC = { paystub: 'Pay stub', bank_statement: 'Bank statement', savings_statement: 'Savings statement', equifax_report: 'Equifax report' };

function ScreeningDocRow({ appId, doc }) {
  const [busy, setBusy] = useState(null);
  const open = async (download) => {
    setBusy(download ? 'd' : 'v');
    try {
      const r = await api.get(`/screening/applications/${appId}/documents/${doc.file_id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      if (download) { const a = document.createElement('a'); a.href = url; a.download = doc.filename || doc.doc_type; a.click(); }
      else window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) { toast.error(apiHelpers.handleError(err)); } finally { setBusy(null); }
  };
  return (
    <div className="px-4 py-3 flex items-center justify-between gap-4" data-testid={`screening-doc-${doc.file_id}`}>
      <div className="flex items-center gap-2.5 min-w-0">
        <div className="w-7 h-7 rounded bg-slate-100 flex items-center justify-center flex-shrink-0"><FileText className="w-3.5 h-3.5 text-slate-500" /></div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-800 truncate">{SCREEN_DOC[doc.doc_type] || doc.doc_type}</p>
          <p className="text-xs text-slate-500 truncate">{doc.filename}</p>
        </div>
      </div>
      <div className="flex items-center gap-4 flex-shrink-0">
        <button onClick={() => open(false)} disabled={!!busy} className="text-xs text-teal-600 hover:text-teal-700 font-medium hover:underline disabled:opacity-50" data-testid={`screening-doc-view-${doc.file_id}`}>{busy === 'v' ? 'Opening…' : 'View'}</button>
        <button onClick={() => open(true)} disabled={!!busy} className="text-xs text-slate-500 hover:text-slate-700 font-medium hover:underline disabled:opacity-50" data-testid={`screening-doc-download-${doc.file_id}`}>Download</button>
      </div>
    </div>
  );
}

const DOC_STATUS = {
  signed: { label: 'Signed', dotColor: 'bg-emerald-500', textColor: 'text-emerald-600' },
  pending: { label: 'Awaiting signature', dotColor: 'bg-amber-500', textColor: 'text-amber-600' },
  declined: { label: 'Declined', dotColor: 'bg-red-500', textColor: 'text-red-600' }
};

function DocumentRow({ doc, formatDate }) {
  const [opening, setOpening] = useState(false);
  const status = DOC_STATUS[doc.status] || DOC_STATUS.pending;

  const openPdf = async () => {
    try {
      setOpening(true);
      const response = await api.get(`/esign/consents/${doc.id}/pdf`, { responseType: 'blob' });
      window.open(URL.createObjectURL(response.data), '_blank', 'noopener');
    } catch (err) {
      toast.error(err.response?.status === 409 ? 'Signed copy is still being prepared. Try again in a minute.' : apiHelpers.handleError(err));
    } finally {
      setOpening(false);
    }
  };

  return (
    <div className="px-4 py-3 flex items-center justify-between gap-4" data-testid={`document-row-${doc.id}`}>
      <div className="flex items-center gap-2.5 min-w-0">
        <div className="w-7 h-7 rounded bg-slate-100 flex items-center justify-center flex-shrink-0">
          <FileText className="w-3.5 h-3.5 text-slate-500" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-800 truncate">
            {doc.title}{doc.test_mode && <span className="ml-2 text-[10px] font-normal text-slate-400 uppercase">Test</span>}
          </p>
          <p className="text-xs text-slate-500 truncate">
            {doc.property_address}{doc.signed_at ? ` · Signed ${formatDate(doc.signed_at)}` : ''}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-4 flex-shrink-0">
        <span className={`inline-flex items-center gap-1.5 text-xs ${status.textColor}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${status.dotColor}`}></span>
          {status.label}
        </span>
        {doc.status === 'signed' && (
          <button
            onClick={openPdf}
            disabled={opening}
            className="text-xs text-teal-600 hover:text-teal-700 font-medium hover:underline disabled:opacity-50"
            data-testid={`document-view-${doc.id}`}
          >
            {opening ? 'Opening…' : 'View PDF'}
          </button>
        )}
      </div>
    </div>
  );
}
